import React, { useState, useEffect, useCallback } from 'react';

function Toast({ message, visible }) {
  return (
    <div style={{
      position: 'fixed', bottom: '1.5rem', left: '50%', transform: `translateX(-50%) translateY(${visible ? 0 : '12px'})`,
      background: '#2c2c2c', color: '#fff', padding: '0.65rem 1.25rem', borderRadius: '24px',
      fontSize: '0.9rem', fontWeight: '500', boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
      opacity: visible ? 1 : 0, transition: 'opacity 0.25s ease, transform 0.25s ease',
      pointerEvents: 'none', zIndex: 9999, whiteSpace: 'nowrap'
    }}>
      {message}
    </div>
  );
}

const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function formatDate(dateStr) {
  if (!dateStr) return null;
  const parts = dateStr.split('-');
  if (parts.length < 3) return dateStr;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  if (isNaN(month) || isNaN(day)) return dateStr;
  if (year <= 1) return `${MONTHS[month]} ${day}`;
  return `${MONTHS[month]} ${day}, ${year}`;
}

function daysInMonth(month, year) {
  if (!month) return 31;
  const m = parseInt(month, 10);
  const y = parseInt(year, 10) || 2000;
  return new Date(y, m, 0).getDate();
}

function isValidDate(month, day, year) {
  if (!month && !day) return true;
  if ((month && !day) || (!month && day)) return false;
  const m = parseInt(month, 10);
  const d = parseInt(day, 10);
  const maxDay = daysInMonth(m, year || 2000);
  if (d < 1 || d > maxDay) return false;
  if (year && year.length > 0 && year.length !== 4) return false;
  if (year && year.length === 4) {
    const y = parseInt(year, 10);
    const today = new Date();
    if (y < 1900) return false;
    if (y > today.getFullYear()) return false;
    if (y === today.getFullYear()) {
      const entered = new Date(y, m - 1, d);
      if (entered > today) return false;
    }
  }
  return true;
}

function buildDateStr(month, day, year) {
  if (!month || !day) return null;
  const y = year && year.length === 4 ? year : '0001';
  return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function parseDateStr(dateStr) {
  if (!dateStr) return { m: '', d: '', y: '' };
  const parts = dateStr.split('-');
  if (parts.length < 3) return { m: '', d: '', y: '' };
  const year = parseInt(parts[0], 10);
  return {
    m: String(parseInt(parts[1], 10) || ''),
    d: String(parseInt(parts[2], 10) || ''),
    y: year <= 1 ? '' : parts[0]
  };
}

function DatePicker({ label, m, d, y, onM, onD, onY }) {
  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <label style={{ fontSize: '0.8rem', color: '#888', display: 'block', marginBottom: '0.25rem' }}>
        {label} <span style={{ color: '#bbb' }}>(year optional)</span>
      </label>
      <div style={{ display: 'flex', gap: '0.4rem' }}>
        <select value={m} onChange={e => onM(e.target.value)} style={{ flex: 2, padding: '0.45rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.85rem', background: '#fff' }}>
          <option value="">Month</option>
          {MONTH_NAMES.map((name, i) => <option key={i} value={String(i + 1)}>{name}</option>)}
        </select>
        <select value={d} onChange={e => onD(e.target.value)} style={{ flex: 1, padding: '0.45rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.85rem', background: '#fff' }}>
          <option value="">Day</option>
          {Array.from({ length: daysInMonth(m, y) }, (_, i) => (
            <option key={i} value={String(i + 1)}>{i + 1}</option>
          ))}
        </select>
        <input
          value={y}
          onChange={e => onY(e.target.value.replace(/\D/g, '').slice(0, 4))}
          placeholder="Year"
          inputMode="numeric"
          style={{ flex: 1, padding: '0.45rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.85rem', textAlign: 'center' }}
        />
      </div>
    </div>
  );
}

function AddContactModal({ userId, onClose, onSaved }) {
  const [step, setStep] = useState('category');
  const [category, setCategory] = useState('');
  const [form, setForm] = useState({ first_name: '', last_name: '', relationship: '', custom_rel: '', birthday_month: '', birthday_day: '', birthday_year: '', notify_pref: 'both' });

  useEffect(() => {
    if (userId) {
      fetch(`/api/users/${userId}`)
        .then(r => r.json())
        .then(u => { if (u && u.notify_pref) setForm(prev => ({ ...prev, notify_pref: u.notify_pref })); })
        .catch(() => {});
    }
  }, [userId]);

  const [showAnniv, setShowAnniv] = useState(false);
  const [annivM, setAnnivM] = useState('');
  const [annivD, setAnnivD] = useState('');
  const [annivY, setAnnivY] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const FAMILY_RELS = ['Mom', 'Dad', 'Sister', 'Brother', 'Spouse / Partner', 'Child', 'Extended family'];
  const FRIEND_RELS = ['Close friend', 'Childhood friend', 'Colleague', 'Mentor', 'Neighbor'];

  const rel = form.relationship === 'Other' ? (form.custom_rel || 'Other') : (form.relationship || '');

  const save = async () => {
    if (!form.first_name.trim() && !rel) { setMsg('Please enter a name or select a relationship.'); return; }
    if (!isValidDate(form.birthday_month, form.birthday_day, form.birthday_year)) {
      setMsg('That birthday doesn\'t look right — please check the date.'); return;
    }
    if (showAnniv && !isValidDate(annivM, annivD, annivY)) {
      setMsg('That anniversary doesn\'t look right — please check the date.'); return;
    }
    setSaving(true);
    setMsg(null);
    const birthday = buildDateStr(form.birthday_month, form.birthday_day, form.birthday_year);
    const anniversary = showAnniv ? buildDateStr(annivM, annivD, annivY) : null;
    try {
      const res = await fetch('/api/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          first_name: form.first_name.trim() || rel,
          last_name: form.last_name.trim() || null,
          relationship: rel || null,
          birthday, anniversary,
          notify_pref: form.notify_pref
        })
      });
      if (res.status === 403) { setMsg('UPGRADE'); setSaving(false); return; }
      if (!res.ok) { setMsg('Something went wrong. Please try again.'); setSaving(false); return; }
      onSaved();
      onClose();
    } catch { setMsg('Something went wrong.'); } finally { setSaving(false); }
  };

  const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' };
  const sheetStyle = { background: '#fff', borderRadius: '16px 16px 0 0', padding: '1.5rem 1.25rem 2rem', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' };
  const chipStyle = (active) => ({ fontSize: '0.85rem', padding: '0.4rem 0.9rem', background: active ? '#5D4037' : '#f0ebe6', color: active ? '#fff' : '#5D4037', borderRadius: '20px', border: 'none', cursor: 'pointer', marginRight: '0.5rem', marginBottom: '0.5rem' });

  if (step === 'category') {
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div style={sheetStyle} onClick={e => e.stopPropagation()}>
          <h3 style={{ marginBottom: '0.5rem', color: '#2c2c2c' }}>Who are you adding?</h3>
          <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1.25rem' }}>This helps us suggest the right relationship options.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {[
              { label: 'Family', desc: 'Parent, sibling, spouse, child, extended family', value: 'family' },
              { label: 'Friend', desc: 'Close friend, childhood friend, colleague, neighbor', value: 'friend' },
              { label: 'Other', desc: 'Someone who doesn\'t fit neatly into a category', value: 'other' }
            ].map(opt => (
              <button key={opt.value} onClick={() => { setCategory(opt.value); setStep('form'); }} style={{ textAlign: 'left', padding: '0.85rem 1rem', background: '#f9f6f2', border: '1px solid #e0d9d2', borderRadius: '10px', cursor: 'pointer' }}>
                <div style={{ fontWeight: '600', color: '#2c2c2c', fontSize: '1rem' }}>{opt.label}</div>
                <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '2px' }}>{opt.desc}</div>
              </button>
            ))}
          </div>
          <button onClick={onClose} style={{ width: '100%', marginTop: '1rem', background: 'transparent', color: '#888', border: 'none', fontSize: '0.9rem', cursor: 'pointer' }}>Cancel</button>
        </div>
      </div>
    );
  }

  const relOptions = category === 'family' ? FAMILY_RELS : category === 'friend' ? FRIEND_RELS : [];

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={sheetStyle} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <button onClick={() => setStep('category')} style={{ background: 'transparent', border: 'none', color: '#888', fontSize: '0.85rem', cursor: 'pointer' }}>← Back</button>
          <h3 style={{ margin: 0, color: '#2c2c2c', fontSize: '1rem' }}>Add {category === 'family' ? 'a family member' : category === 'friend' ? 'a friend' : 'a contact'}</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#888', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
        </div>

        {relOptions.length > 0 && (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
              {relOptions.map(r => (
                <button key={r} type="button" onClick={() => setForm({ ...form, relationship: form.relationship === r ? '' : r })} style={chipStyle(form.relationship === r)}>{r}</button>
              ))}
              <button type="button" onClick={() => setForm({ ...form, relationship: form.relationship === 'Other' ? '' : 'Other', custom_rel: '' })} style={chipStyle(form.relationship === 'Other')}>Other</button>
            </div>
            {form.relationship === 'Other' && (
              <input value={form.custom_rel} onChange={e => setForm({ ...form, custom_rel: e.target.value })} placeholder="Describe the relationship (e.g. neighbor's child)" style={{ width: '100%', marginBottom: '0.5rem' }} />
            )}
          </div>
        )}

        {category === 'other' && (
          <div style={{ marginBottom: '0.75rem' }}>
            <input value={form.custom_rel} onChange={e => setForm({ ...form, custom_rel: e.target.value, relationship: 'Other' })} placeholder="Describe the relationship (e.g. mentor, neighbor)" style={{ width: '100%' }} />
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder={form.relationship ? 'First name (optional)' : 'First name'} style={{ flex: 1 }} />
          <input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Last name (optional)" style={{ flex: 1 }} />
        </div>

        <DatePicker
          label="Birthday"
          m={form.birthday_month} d={form.birthday_day} y={form.birthday_year}
          onM={v => setForm(prev => {
            const maxDay = daysInMonth(v, prev.birthday_year);
            return { ...prev, birthday_month: v, birthday_day: prev.birthday_day && parseInt(prev.birthday_day) > maxDay ? '' : prev.birthday_day };
          })}
          onD={v => setForm(prev => ({ ...prev, birthday_day: v }))}
          onY={v => setForm(prev => ({ ...prev, birthday_year: v }))}
        />

        {!showAnniv ? (
          <button type="button" onClick={() => setShowAnniv(true)} style={{ background: 'transparent', color: '#5D4037', fontSize: '0.85rem', padding: '0.3rem 0', border: 'none', cursor: 'pointer', marginBottom: '0.75rem', display: 'block' }}>+ Add anniversary</button>
        ) : (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.2rem' }}>
              <span style={{ fontSize: '0.8rem', color: '#888' }}>Anniversary</span>
              <button type="button" onClick={() => { setShowAnniv(false); setAnnivM(''); setAnnivD(''); setAnnivY(''); }} style={{ background: 'transparent', color: '#999', fontSize: '0.8rem', border: 'none', cursor: 'pointer' }}>Remove</button>
            </div>
            <DatePicker label="" m={annivM} d={annivD} y={annivY} onM={setAnnivM} onD={setAnnivD} onY={setAnnivY} />
          </div>
        )}

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ fontSize: '0.8rem', color: '#888', display: 'block', marginBottom: '0.3rem' }}>Remind via</label>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {[['sms', 'Text'], ['email', 'Email'], ['both', 'Both']].map(([val, label]) => (
              <button key={val} type="button" onClick={() => setForm({ ...form, notify_pref: val })} style={{ flex: 1, fontSize: '0.8rem', padding: '0.35rem', background: form.notify_pref === val ? '#2c2c2c' : '#f0ebe6', color: form.notify_pref === val ? '#fff' : '#5D4037', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>{label}</button>
            ))}
          </div>
        </div>

        {msg === 'UPGRADE' ? (
          <div style={{ background: '#fdf6ee', border: '1px solid #d4a96a', borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '0.75rem' }}>
            <div style={{ fontWeight: '600', color: '#5D4037', fontSize: '0.88rem', marginBottom: '0.25rem' }}>You've reached the 25-contact limit</div>
            <div style={{ fontSize: '0.82rem', color: '#7a5c45', marginBottom: '0.6rem' }}>Upgrade to Premium for unlimited contacts — $20/year.</div>
            <a href="/upgrade" style={{ display: 'inline-block', background: '#5D4037', color: '#fff', padding: '0.45rem 1rem', borderRadius: '6px', fontSize: '0.82rem', fontWeight: '600', textDecoration: 'none' }}>
              Go Premium →
            </a>
          </div>
        ) : msg ? (
          <div style={{ color: '#c62828', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{msg}</div>
        ) : null}

        <button onClick={save} disabled={saving || (!form.first_name.trim() && !rel && !form.custom_rel.trim())} style={{ width: '100%', background: '#5D4037', opacity: (form.first_name.trim() || rel || form.custom_rel.trim()) ? 1 : 0.5 }}>
          {saving ? 'Saving...' : 'Add Contact'}
        </button>
      </div>
    </div>
  );
}

function AccountDetails({ userId, onToast }) {
  const [user, setUser] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editEmail, setEditEmail] = useState('');
  const [editPref, setEditPref] = useState('both');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = () => {
    fetch(`/api/users/${userId}`).then(r => r.json()).then(u => {
      setUser(u);
      setEditEmail(u.email || '');
      setEditPref(u.notify_pref || 'both');
    });
  };

  useEffect(() => { if (userId) load(); }, [userId]);

  const save = async () => {
    setSaving(true); setMsg(null);
    const res = await fetch(`/api/users/${userId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: editEmail.trim() || null, notify_pref: editPref })
    });
    if (res.ok) { setEditing(false); load(); if (onToast) onToast('Account updated.'); }
    else { const d = await res.json(); setMsg(d.error || 'Could not save changes.'); }
    setSaving(false);
  };

  if (!user) return <p style={{ color: '#aaa' }}>Loading...</p>;

  const fieldStyle = { marginBottom: '1.25rem' };
  const labelStyle = { fontSize: '0.78rem', color: '#999', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '0.3rem' };
  const valueStyle = { fontSize: '1rem', color: '#2c2c2c' };
  const inputStyle = { width: '100%', padding: '0.55rem 0.85rem', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.95rem', boxSizing: 'border-box' };

  return (
    <div style={{ maxWidth: '480px' }}>
      <div className="card" style={{ padding: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0, color: '#5D4037' }}>Account Details</h3>
          {!editing && (
            <button onClick={() => setEditing(true)} style={{ background: 'transparent', color: '#5D4037', border: '1px solid #5D4037', borderRadius: '6px', padding: '0.35rem 0.85rem', fontSize: '0.85rem', cursor: 'pointer' }}>Edit</button>
          )}
        </div>

        <div style={fieldStyle}>
          <span style={labelStyle}>Name</span>
          <span style={valueStyle}>{user.name}</span>
        </div>

        <div style={fieldStyle}>
          <span style={labelStyle}>Phone</span>
          <span style={valueStyle}>{user.phone}</span>
        </div>

        <div style={fieldStyle}>
          <span style={labelStyle}>Email</span>
          {editing ? (
            <input type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)}
              placeholder="your@email.com" style={inputStyle} />
          ) : (
            <span style={valueStyle}>{user.email || <span style={{ color: '#bbb' }}>Not set</span>}</span>
          )}
        </div>

        <div style={fieldStyle}>
          <span style={labelStyle}>Reminder Notifications</span>
          {editing ? (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[['sms', '💬 Text'], ['email', '📧 Email'], ['both', '💬📧 Both']].map(([val, label]) => (
                <button key={val} type="button" onClick={() => setEditPref(val)}
                  style={{ flex: 1, fontSize: '0.85rem', padding: '0.45rem 0.5rem', background: editPref === val ? '#5D4037' : '#f0ebe6', color: editPref === val ? '#fff' : '#5D4037', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
                  {label}
                </button>
              ))}
            </div>
          ) : (
            <span style={valueStyle}>
              {user.notify_pref === 'sms' ? '💬 Text' : user.notify_pref === 'email' ? '📧 Email' : '💬📧 Both'}
            </span>
          )}
        </div>

        {msg && <div style={{ color: '#c62828', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{msg}</div>}

        {editing && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={save} disabled={saving} style={{ background: '#5D4037', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.6rem 1.25rem', fontSize: '0.9rem', cursor: 'pointer' }}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button onClick={() => { setEditing(false); setMsg(null); setEditEmail(user.email || ''); setEditPref(user.notify_pref || 'both'); }}
              style={{ background: '#f0ebe6', color: '#5D4037', border: 'none', borderRadius: '8px', padding: '0.6rem 1.25rem', fontSize: '0.9rem', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Contacts({ userId }) {
  const [tab, setTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [editBdayM, setEditBdayM] = useState('');
  const [editBdayD, setEditBdayD] = useState('');
  const [editBdayY, setEditBdayY] = useState('');
  const [editAnnivM, setEditAnnivM] = useState('');
  const [editAnnivD, setEditAnnivD] = useState('');
  const [editAnnivY, setEditAnnivY] = useState('');
  const [showEditAnniv, setShowEditAnniv] = useState(false);
  const [editMsg, setEditMsg] = useState(null);
  const [giftForm, setGiftForm] = useState(null);
  const [giftIdea, setGiftIdea] = useState('');
  const [giftUrl, setGiftUrl] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimer = React.useRef(null);

  const showToast = useCallback((msg) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToastMsg(msg);
    setToastVisible(true);
    toastTimer.current = setTimeout(() => setToastVisible(false), 2800);
  }, []);

  const reload = () => {
    if (userId) fetch(`/api/contacts/user/${userId}`).then(r => r.json()).then(setContacts);
  };

  useEffect(() => { reload(); }, [userId]);

  if (!userId) return <p>Please sign up first.</p>;

  const startEdit = (c) => {
    const bday = parseDateStr(c.birthday);
    const anniv = parseDateStr(c.anniversary);
    setForm({ first_name: c.first_name, last_name: c.last_name || '', relationship: c.relationship || '', notify_pref: c.notify_pref || 'both' });
    setEditBdayM(bday.m); setEditBdayD(bday.d); setEditBdayY(bday.y);
    setEditAnnivM(anniv.m); setEditAnnivD(anniv.d); setEditAnnivY(anniv.y);
    setShowEditAnniv(!!c.anniversary);
    setEditMsg(null);
    setEditing(c.id);
  };

  const save = async (id) => {
    if (!isValidDate(editBdayM, editBdayD, editBdayY)) { setEditMsg('That birthday doesn\'t look right.'); return; }
    if (showEditAnniv && !isValidDate(editAnnivM, editAnnivD, editAnnivY)) { setEditMsg('That anniversary doesn\'t look right.'); return; }
    const birthday = buildDateStr(editBdayM, editBdayD, editBdayY);
    const anniversary = showEditAnniv ? buildDateStr(editAnnivM, editAnnivD, editAnnivY) : null;
    await fetch(`/api/contacts/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, birthday, anniversary })
    });
    setEditing(null);
    setEditMsg(null);
    reload();
    showToast('Changes saved.');
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this contact?')) return;
    const res = await fetch(`/api/contacts/${id}`, { method: 'DELETE' });
    const data = await res.json();
    if (data.success) {
      setContacts(contacts.filter(c => c.id !== id));
    }
  };

  const addGift = async (contactId) => {
    if (!giftIdea.trim()) return;
    const res = await fetch(`/api/contacts/${contactId}/gifts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idea: giftIdea.trim(), url: giftUrl.trim() || null })
    });
    if (res.ok) {
      setGiftForm(null); setGiftIdea(''); setGiftUrl('');
      reload();
      showToast('Gift idea added.');
    } else {
      const data = await res.json();
      alert(data.error || 'Could not add gift idea');
    }
  };

  const removeGift = async (giftId) => {
    await fetch(`/api/contacts/gifts/${giftId}`, { method: 'DELETE' });
    reload();
    showToast('Gift idea removed.');
  };

  const notifyLabel = (pref) => {
    if (pref === 'sms') return '💬 Text';
    if (pref === 'email') return '📧 Email';
    return '💬📧 Both';
  };

  const tabStyle = (active) => ({
    padding: '0.5rem 1.25rem', fontSize: '0.9rem', fontWeight: active ? '600' : '400',
    color: active ? '#5D4037' : '#999', background: 'transparent', border: 'none',
    borderBottom: active ? '2px solid #5D4037' : '2px solid transparent',
    cursor: 'pointer', marginBottom: '-1px'
  });

  return (
    <div>
      <Toast message={toastMsg} visible={toastVisible} />

      {showModal && (
        <AddContactModal
          userId={userId}
          onClose={() => setShowModal(false)}
          onSaved={() => { reload(); showToast('Contact added!'); }}
        />
      )}

      <div style={{ display: 'flex', borderBottom: '1px solid #e8e0d8', marginBottom: '1.25rem' }}>
        <button style={tabStyle(tab === 'contacts')} onClick={() => setTab('contacts')}>Contacts</button>
        <button style={tabStyle(tab === 'account')} onClick={() => setTab('account')}>Account Details</button>
      </div>

      {tab === 'account' && <AccountDetails userId={userId} onToast={showToast} />}

      {tab === 'contacts' && <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>Contacts ({contacts.length})</h2>
        <button onClick={() => setShowModal(true)} style={{ background: '#6B7F5E', fontSize: '0.9rem', padding: '0.5rem 1rem', borderRadius: '8px' }}>
          + Add contact
        </button>
      </div>

      {contacts.map(c => (
        <div className="card" key={c.id}>
          {editing === c.id ? (
            <div>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input value={form.first_name || ''} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="First name" style={{ flex: 1 }} />
                <input value={form.last_name || ''} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Last name" style={{ flex: 1 }} />
              </div>
              <input value={form.relationship || ''} onChange={e => setForm({ ...form, relationship: e.target.value })} placeholder="Relationship" style={{ marginBottom: '0.5rem', width: '100%' }} />

              <DatePicker
                label="Birthday"
                m={editBdayM} d={editBdayD} y={editBdayY}
                onM={v => { setEditBdayM(v); if (editBdayD && daysInMonth(v, editBdayY) < parseInt(editBdayD)) setEditBdayD(''); }}
                onD={setEditBdayD}
                onY={setEditBdayY}
              />

              {!showEditAnniv ? (
                <button type="button" onClick={() => setShowEditAnniv(true)} style={{ background: 'transparent', color: '#5D4037', fontSize: '0.85rem', padding: '0.3rem 0', border: 'none', cursor: 'pointer', marginBottom: '0.5rem', display: 'block' }}>+ Add anniversary</button>
              ) : (
                <div style={{ marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.2rem' }}>
                    <span style={{ fontSize: '0.8rem', color: '#888' }}>Anniversary</span>
                    <button type="button" onClick={() => { setShowEditAnniv(false); setEditAnnivM(''); setEditAnnivD(''); setEditAnnivY(''); }} style={{ background: 'transparent', color: '#999', fontSize: '0.8rem', border: 'none', cursor: 'pointer' }}>Remove</button>
                  </div>
                  <DatePicker label="" m={editAnnivM} d={editAnnivD} y={editAnnivY} onM={v => { setEditAnnivM(v); if (editAnnivD && daysInMonth(v, editAnnivY) < parseInt(editAnnivD)) setEditAnnivD(''); }} onD={setEditAnnivD} onY={setEditAnnivY} />
                </div>
              )}

              <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: '0.4rem' }}>Remind via</label>
              <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.75rem' }}>
                {[['sms', 'Text'], ['email', 'Email'], ['both', 'Both']].map(([val, label]) => (
                  <button key={val} type="button" onClick={() => setForm({ ...form, notify_pref: val })} style={{ flex: 1, fontSize: '0.8rem', padding: '0.35rem', background: (form.notify_pref || 'both') === val ? '#2c2c2c' : '#f0ebe6', color: (form.notify_pref || 'both') === val ? '#fff' : '#5D4037', borderRadius: '4px', border: 'none', cursor: 'pointer' }}>
                    {label}
                  </button>
                ))}
              </div>

              {editMsg && <div style={{ color: '#c62828', fontSize: '0.85rem', marginBottom: '0.5rem' }}>{editMsg}</div>}
              <button onClick={() => save(c.id)}>Save</button>{' '}
              <button onClick={() => { setEditing(null); setEditMsg(null); }} style={{ background: '#999' }}>Cancel</button>
            </div>
          ) : (
            <div>
              <h3 style={{ marginBottom: '0.25rem' }}>{c.first_name} {c.last_name || ''}</h3>
              <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem', lineHeight: '1.6' }}>
                {c.relationship && <span>{c.relationship}</span>}
                {c.relationship && (c.birthday || c.anniversary) && <span style={{ color: '#ccc', margin: '0 0.4rem' }}>·</span>}
                {c.birthday && <span>🎂 {formatDate(c.birthday)}</span>}
                {c.birthday && c.anniversary && <span style={{ color: '#ccc', margin: '0 0.4rem' }}>·</span>}
                {c.anniversary && <span>💍 {formatDate(c.anniversary)}</span>}
                <span style={{ fontSize: '0.8rem', color: '#999', marginLeft: c.relationship || c.birthday || c.anniversary ? '0.4rem' : 0 }}>
                  {(c.relationship || c.birthday || c.anniversary) && <span style={{ color: '#ccc', marginRight: '0.4rem' }}>·</span>}
                  {notifyLabel(c.notify_pref || 'both')}
                </span>
              </p>
              {c.gift_ideas && c.gift_ideas.filter(g => g.id).map(g => (
                <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <span className="gift">🎁 {g.idea} {g.url && <a href={g.url} target="_blank" rel="noreferrer">→ link</a>}</span>
                  <button onClick={() => removeGift(g.id)} style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem', background: '#c62828', lineHeight: 1 }}>✕</button>
                </div>
              ))}
              {giftForm === c.id ? (
                <div style={{ marginTop: '0.5rem' }}>
                  <input value={giftIdea} onChange={e => setGiftIdea(e.target.value)} placeholder="Gift idea (e.g. Buffalo Bills jersey)" style={{ marginBottom: '0.25rem' }} />
                  <input value={giftUrl} onChange={e => setGiftUrl(e.target.value)} placeholder="Link (optional)" style={{ marginBottom: '0.25rem' }} />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => addGift(c.id)} style={{ fontSize: '0.8rem', padding: '0.3rem 0.8rem' }}>Save Gift Idea</button>
                    <button onClick={() => { setGiftForm(null); setGiftIdea(''); setGiftUrl(''); }} style={{ fontSize: '0.8rem', padding: '0.3rem 0.8rem', background: '#999' }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button onClick={() => startEdit(c)} style={{ fontSize: '0.8rem', padding: '0.3rem 0.8rem' }}>Edit</button>
                  <button onClick={() => { setGiftForm(c.id); setGiftIdea(''); setGiftUrl(''); }} style={{ fontSize: '0.8rem', padding: '0.3rem 0.8rem', background: '#5d4037' }}>Add Gift Idea</button>
                  <button onClick={() => remove(c.id)} style={{ fontSize: '0.8rem', padding: '0.3rem 0.8rem', background: '#c62828' }}>Delete</button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      </>}
    </div>
  );
}
